// Vanilla JavaScript Toast Utility using DaisyUI components
// Creates and manages toast notifications with DaisyUI styling

export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
}

const TOAST_CONFIG = {
  [TOAST_TYPES.SUCCESS]: {
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`,
    className: 'alert-success'
  },
  [TOAST_TYPES.ERROR]: {
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`,
    className: 'alert-error'
  },
  [TOAST_TYPES.WARNING]: {
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
    </svg>`,
    className: 'alert-warning'
  },
  [TOAST_TYPES.INFO]: {
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`,
    className: 'alert-info'
  }
}

class ToastManager {
  constructor() {
    this.toastContainer = null
    this.toastCounter = 0
    this.initializeContainer()
  }

  initializeContainer() {
    // Create or find the toast container
    this.toastContainer = document.getElementById('toast-container')

    if (!this.toastContainer) {
      this.toastContainer = document.createElement('div')
      this.toastContainer.id = 'toast-container'
      this.toastContainer.className = 'toast toast-top toast-end z-50 fixed'
      this.toastContainer.style.cssText = 'position: fixed; top: 1rem; right: 1rem; z-index: 9999;'
      document.body.appendChild(this.toastContainer)
    }
  }

  show(message, type = TOAST_TYPES.INFO, duration = 5000) {
    // Ensure container exists
    this.initializeContainer()

    const config = TOAST_CONFIG[type] || TOAST_CONFIG[TOAST_TYPES.INFO]
    const toastId = `toast-${++this.toastCounter}`

    const toastElement = document.createElement('div')
    toastElement.id = toastId
    toastElement.className = `alert ${config.className} shadow-lg mb-2 max-w-sm`
    toastElement.style.cssText = 'animation: slideInRight 0.3s ease-out;'

    toastElement.innerHTML = `
      <div class="flex items-center">
        ${config.icon}
        <span class="ml-2">${message}</span>
      </div>
      <div class="flex-none ml-4">
        <button class="btn btn-sm btn-ghost btn-circle" data-toast-close="${toastId}">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `

    // Add click handler for close button
    const closeButton = toastElement.querySelector(`[data-toast-close="${toastId}"]`)
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.hide(toastId)
      })
    }

    // Add to container
    this.toastContainer.appendChild(toastElement)

    // Auto-hide after duration
    if (duration > 0) {
      setTimeout(() => this.hide(toastId), duration)
    }

    return toastId
  }

  hide(toastId) {
    const toastElement = document.getElementById(toastId)
    if (toastElement) {
      toastElement.style.cssText += 'animation: slideOutRight 0.3s ease-out;'

      setTimeout(() => {
        if (toastElement.parentNode) {
          toastElement.parentNode.removeChild(toastElement)
        }
      }, 300)
    }
  }

  hideAll() {
    if (this.toastContainer) {
      this.toastContainer.innerHTML = ''
    }
  }

  // Convenience methods
  success(message, duration = 5000) {
    return this.show(message, TOAST_TYPES.SUCCESS, duration)
  }

  error(message, duration = 8000) {
    return this.show(message, TOAST_TYPES.ERROR, duration)
  }

  warning(message, duration = 6000) {
    return this.show(message, TOAST_TYPES.WARNING, duration)
  }

  info(message, duration = 5000) {
    return this.show(message, TOAST_TYPES.INFO, duration)
  }
}


export const toast = new ToastManager()